import { ButtonGroup } from 'components/ButtonGroup';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Menu } from 'components/Menu';
import { ModalScreens } from 'components/Navigation/types';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { pureComponent } from 'lib/pureComponent';
import { randomKey } from 'lib/riskbase';
import { smartText } from 'lib/str';
import React from 'react';
import { Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import { FileTypeGroupsEnum } from 'service/file/types';
import {
  doAppFileGetFilesFromPicker,
  doAppFileGetImagesFromPicker,
  eventFilesAdded,
  doSetupOrganisationSaveLogo,
  ALERT,
  NAVIGATE_MODAL,
} from 'store/actions';

export const OrganisationLogoButtons: React.FC = pureComponent(() => {
  const dispatch = useDispatch();
  const eventKey = React.useRef(randomKey());

  React.useEffect(() => {
    const events = [
      doAddEvent(eventFilesAdded, (event) => {
        if (event.payload.key === eventKey.current) {
          const file = event.payload.files[0];
          if (file) {
            dispatch(
              doSetupOrganisationSaveLogo({
                file,
              }),
            );
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch]);

  const buttonChildren = [];
  const otherButtonsConfig: {
    key: string;
    text: string;
    icon: ICONS;
    onPress: () => void;
    disabled?: boolean;
    destructive?: boolean;
  }[] = [];

  buttonChildren.push(
    <IconButton
      key="image"
      icon={ICONS.ImageLight}
      onPress={() =>
        dispatch(
          doAppFileGetImagesFromPicker({
            eventKey: eventKey.current,
            multiSelect: false,
            returnType: 'event',
          }),
        )
      }
    />,
  );

  if (Platform.OS !== 'web') {
    buttonChildren.push(
      <IconButton
        key="file"
        icon={ICONS.FolderOpenLight}
        onPress={() =>
          dispatch(
            doAppFileGetFilesFromPicker({
              eventKey: eventKey.current,
              multiSelect: false,
              allowedFileGroups: [FileTypeGroupsEnum.image],
              returnType: 'event',
            }),
          )
        }
      />,
    );
  }

  otherButtonsConfig.push({
    key: 'info',
    text: 'Logo Information',
    icon: ICONS.CircleInfoLight,
    onPress: () =>
      dispatch(
        ALERT({
          title: 'Organisation Logo',
          text: smartText(
            'Your logo will look best on reports uploaded as an SVG. ' +
              'This file type can be scaled up and down without losing quality. ' +
              'You may have been provided with an SVG by the logo designer.\n\n' +
              "If you don't have an SVG then a normal PNG image or JPEG image can be uploaded.\n\n" +
              'For help, get in touch with RiskBase Support',
          ),
          altButton: {
            text: 'Contact Support',
            closeAlert: true,
            action: NAVIGATE_MODAL({
              screen: ModalScreens.Support,
              params: {},
            }),
          },
        }),
      ),
  });

  otherButtonsConfig.push({
    key: 'remove',
    text: 'Remove Logo',
    icon: ICONS.TrashCanLight,
    destructive: true,
    onPress: () =>
      dispatch(
        doSetupOrganisationSaveLogo({
          file: null,
        }),
      ),
  });

  if (otherButtonsConfig.length) {
    buttonChildren.push(
      <Menu
        key="other"
        iconButtonConfig={{
          icon: ICONS.EllipsisLight,
        }}
        width={200}
        options={otherButtonsConfig}
      />,
    );
  }

  if (!buttonChildren.length) {
    return null;
  }

  return <ButtonGroup>{buttonChildren}</ButtonGroup>;
});
